import { MESSAGE_TYPE, TypeMessageLayout, TypeStructure } from "../enums";
import { IMessage } from "../interfaces";
import { IComponent, ISettingsStructure, MessageStructure, ScrollPosition, StateOption } from "../types";
import { Util } from "../util";
import { isValidModalCarouselList } from "./validate-modal-carousel";
import { __vueEvent, __oneMinute } from "../constant";
import { EVENTS } from "../enums/events";

export const appendMessage = function (state: StateOption, value: IMessage) {
	/* SD-5915: Since the message showing in modal are not included in the chat container excluding it from the actual list */
	if (isValidModalCarouselList(value)) {
		/**
		 * JIRA: SD-7859
		 * As part of incorporate the markdown message above the carousel. The modal layout carousel was broken.
		 * So instead of depends on `index`, validating the component dynamically type === "carousel"
		 */
		const carouselStructure = (value.contents.structures as MessageStructure[]).find(({ components }) =>
			components.some((component) => !Util.isNullOrEmpty(component.type) && component.type === "carousel")
		);
		if (carouselStructure) {
			const { components, type, settings } = carouselStructure;
			state.carouselModalList = {
				components: components as IComponent[],
				type: type as TypeStructure,
				settings: settings as ISettingsStructure,
				layout: value.contents.layout as TypeMessageLayout,
			};
		}
	} else {
		//SD-8744: If welcome message move it to top of messages array
		let isWelcomeMessage = false;
		let isStreaming = false;
		if (
			value &&
			value.contents &&
			value.contents.recordInfo &&
			value.contents.recordInfo.messageCacheId &&
			value.contents.recordInfo.messageCacheId === "1"
		) {
			isWelcomeMessage = true;
		}

		isStreaming = updateMessageComponent(state, value);

		state.isStreamingMessages = isStreaming;

		if(!isStreaming) {
			if (isWelcomeMessage) {
				state.messages.unshift(value);
			} else {
				// Add the message to the chat screen
				state.messages.push(value);
			}
		}
		/* SD-7153 On append a new message, need to remove the existing carousel model */
		if (state.carouselModalDetail) state.carouselModalDetail = null;
		if (state.carouselModalList) state.carouselModalList = null;
	}
};

const updateMessageComponent = function (state: StateOption, value: IMessage) {
	try	{
		let isStreaming = false;
		/*
			1. Always getting one structure
			2. Multiple components supported
		*/
		if (
			value &&
			value.contents &&
			value.contents.keys &&
			value.contents.keys.streamingMessageId &&
			value.contents.keys.streamingMessageId.length !== 0
		) {

			const msg = state.messages.find(message => {
  			return message?.contents?.keys?.streamingMessageId === value?.contents?.keys?.streamingMessageId;
			});

			if (msg != null) {
				isStreaming = true;
				//this is not needed any more
				//__vueEvent.emit(EVENTS.ScrollTopToLastUserMessage);
					value.contents.structures[0].components.forEach(element => {
						if(!Array.isArray(msg.contents.structures[0].components)) {
							msg.contents.structures[0].components = [];
						}

						try {
								const structures = msg.contents.structures;
								if (!structures?.length) throw new Error("No structures found");

								const components = structures[0].components;
								if (!components?.length) throw new Error("No components found");

								const lastComponent = components[components.length - 1];

								if (lastComponent.type === 'text') {
									lastComponent.content += element.content;
								} else {
									components.push(element);
								}
							} catch (ex) {
								msg.contents.structures?.[0]?.components?.push(element);
							}

					});

					try	{
						/* For Quick Reply*/
						const quickReplies = value?.contents?.quickReplies;

						if (Array.isArray(quickReplies) && quickReplies.length > 0) {
							msg.contents.quickReplies = msg.contents.quickReplies || [];
							msg.contents.quickReplies.push(...quickReplies);
						}
					} catch(ex){
						console.error("Quick Reply Insert Failed!" , ex);
					}
			}
		}
		return isStreaming;
	} catch(ex){
		console.error("Update Message Content Failed!" , ex);
	}
	return false;
}

/**
 * Determines whether to scroll to the anchor of the first agent message
 * after the latest user message based on the incoming message and its settings.
 *
 * @param state - The current state of the chat, including the list of messages.
 * @param value - The incoming message that may trigger a scroll action.
 * @returns An object containing the incoming scroll position, whether to scroll to the first agent message anchor, and if the scroll position is top.
 */
export const getAddMessageAnchorScroll = (state: StateOption, value: IMessage) => {
  const incomingScrollPosition: ScrollPosition = value?.contents?.settings?.scrollPosition || "default";
  const isTopAnchorScrollPosition = incomingScrollPosition === "top" || incomingScrollPosition === "near_top";

  try {
    const isAgentMessage = value?.type === MESSAGE_TYPE.Bot || value?.type === MESSAGE_TYPE.Agent;
    const latestUserMessageIndex = state.messages.map((message) => message.type).lastIndexOf(MESSAGE_TYPE.User);
    const hasAgentAfterLatestUser =
      latestUserMessageIndex >= 0 &&
      state.messages
        .slice(latestUserMessageIndex + 1)
        .some((message) => message.type === MESSAGE_TYPE.Bot || message.type === MESSAGE_TYPE.Agent);
    const shouldScrollFirstAgentToAnchor =
      isAgentMessage && latestUserMessageIndex >= 0 && !hasAgentAfterLatestUser && isTopAnchorScrollPosition;

    return {
      incomingScrollPosition,
      shouldScrollFirstAgentToAnchor,
      isTopAnchorScrollPosition,
    };
  } catch (ex) {
    return { incomingScrollPosition, shouldScrollFirstAgentToAnchor: false, isTopAnchorScrollPosition };
  }
};

/**
 * Checks if the incoming message is a streaming message by looking for a streamingMessageId 
 * in its contents keys and comparing it with existing messages in the state.
 */
export const isMessageStreaming = (state: StateOption, value: IMessage): boolean => {
  try {
    const id = value?.contents?.keys?.streamingMessageId;
    if (!id?.length) return false;    
    return state.messages?.some((m) => m?.contents?.keys?.streamingMessageId === id) ?? false;
  } catch (ex) {
    console.error("Error determining if message is streaming:", ex);
    return false;
  }
};
